import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown } from 'antd';
import { DotsThree } from '@phosphor-icons/react';
import Modal from '../Modal';
import { usePermissions } from '../../context/PermissionsContext';
import { getSandboxState } from '../../services/SandboxService';
import { markSleptByChoice } from '../../utils/sandboxSleep';
import {
  clearMyPreviewCaches, deleteMySandbox, getMySandboxManage, restartMySandbox, sleepMySandbox,
} from '../../services/MySandboxService';
import './ManageSandbox.css';

/**
 * The person manages their own sandbox (vfs-service src/gateway/mySandbox.js, docs/sandbox-self-service.md): Restart,
 * Put to sleep now, Clear preview caches and Delete sandbox…, in Settings › Sandbox (ManageSandboxSection) and in the
 * "⋯" menu of Home's sandbox card (ManageSandboxMenu). Each action says what happens, shows its progress, then reads
 * the state again. Delete keeps the disks unless the person ticks "Also delete my disks" and types the sandbox's name.
 * Nothing shows where the gateway does not offer it (older, shared mode), for someone with no sandbox, or for a viewer.
 * A new plan or sandbox type the running pod does not have yet (the overview's `pending`, vfs-service
 * admin/pendingStart.js) is applied by Restart (the gateway puts the sandbox to sleep and wakes it): both places say so.
 */

export const ACTIONS = [
  { key: 'restart', label: 'Restart', what: 'Stops and starts your sandbox again, in about a minute. Running previews and terminals stop; your files and data stay.' },
  { key: 'sleep', label: 'Put to sleep now', what: 'Stops it now instead of when it has been idle a while. It wakes up the next time you open a project, and the apps that were running start again.' },
  { key: 'clearCaches', label: 'Clear preview caches', what: 'Stops every preview and removes their working copies, stopped containers and leftover Docker files. Your code and data are not touched.' },
  { key: 'delete', label: 'Delete sandbox…', what: 'Deletes the sandbox. By default your disks are kept and your sandbox is made again with them the next time you open Flow.', danger: true },
];

const POLL_MS = 2000;
const RESTART_MAX_MS = 3 * 60_000;
const PENDING_POLL_MS = 10_000;
const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/** Why an action cannot run now (null: it can). */
export function blockedReason(key, info) {
  if (!info) { return 'Loading…'; }
  if (info.pendingDelete) { return 'Your sandbox is being deleted.'; }
  if (info.state === 'none') { return 'You have no sandbox right now.'; }
  if (key === 'delete') { return null; }
  if (info.state === 'asleep') { return key === 'sleep' ? 'Your sandbox is already asleep.' : 'Your sandbox is asleep: nothing runs.'; }
  if (info.state !== 'running') { return 'Your sandbox is starting or going to sleep: try again in a moment.'; }
  return null;
}

/** "kept 30 days" from the plan's own-backup retention. */
export function keptText(backups) {
  if (!backups?.keepDays) { return 'kept by your plan’s backup retention'; }
  return `kept ${backups.keepDays} day${backups.keepDays === 1 ? '' : 's'}${backups.keepLast ? ` (or until you have ${backups.keepLast} newer backups of your own)` : ''}`;
}

// The kinds of a capability change (the gateway's admin/pendingStart.js specChanges), in words.
const KIND_WORDS = { docker: 'Docker', internet: 'internet access', resources: 'resources', type: 'sandbox type', settings: 'settings' };

/**
 * "your new plan Team" / "your plan’s new limits" and/or "your new sandbox type Java"; with neither, "your plan’s new
 * capabilities (Docker)" from `settings.changes` (any other difference of the pod: Docker turned on alone...); and
 * whether that is plural.
 */
function pendingWords(pending) {
  const plan = pending?.plan || null;
  const type = pending?.type || null;
  const newPlan = Boolean(plan && plan.to && plan.to !== plan.from);
  const parts = [
    plan ? (newPlan ? `your new plan ${plan.to}` : 'your plan’s new limits') : null,
    type ? `your new sandbox type ${type.name || type.to}` : null,
  ].filter(Boolean);
  const kinds = (pending?.settings?.changes || []).map((kind) => KIND_WORDS[kind] || kind);
  if (!parts.length && kinds.length) { return { text: `your plan’s new capabilities (${kinds.join(', ')})`, plural: true }; }
  return { text: parts.join(' and '), plural: parts.length > 1 || Boolean(plan && !newPlan) };
}

/**
 * "Your new plan Team applies at the next restart." from the overview's `pending: { plan, type }` (plan/type:
 * `{ from, to }`, type also `name`); '' when nothing is pending.
 */
export function pendingRestartText(pending) {
  const { text, plural } = pendingWords(pending);
  if (!text) { return ''; }
  return `${text.charAt(0).toUpperCase()}${text.slice(1)} ${plural ? 'apply' : 'applies'} at the next restart.`;
}

/** What the restart said it applied (`applied`, as `pending`; `recreated`: made again on its disks), once it is done. */
function restartedText(applied) {
  const { text } = pendingWords(applied);
  const remade = applied?.recreated ? ' It was made again on your disks.' : '';
  return `Your sandbox restarted${text ? ` with ${text}` : ''}.${remade} Your files and data are as they were.`;
}

function clearedText(out) {
  const parts = [];
  if (typeof out?.sessions === 'number') { parts.push(`${out.sessions} preview${out.sessions === 1 ? '' : 's'} stopped and cleared`); }
  if (out?.folders) { parts.push(`${out.folders} leftover folder${out.folders === 1 ? '' : 's'} removed`); }
  if (out?.docker?.available) { parts.push('stopped containers and dangling Docker files pruned'); }
  const done = parts.length ? `Preview caches cleared: ${parts.join(', ')}.` : 'Preview caches cleared.';
  return out?.partial ? `${done} Your sandbox’s preview runner is older: leftover folders and Docker files are cleared once it is updated.` : done;
}

/** The overview, the action under way, what was said about it, and the actions themselves. */
export function useManageSandbox({ onChanged = null, reloadKey = null } = {}) {
  const [info, setInfo] = useState(undefined); // undefined: loading; null: not offered here
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState(null); // { tone: 'info' | 'ok' | 'error', text }
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(() => Promise.resolve().then(() => getMySandboxManage())
    .then((next) => { if (alive.current) { setInfo(next); } return next; })
    .catch(() => { if (alive.current) { setInfo((current) => (current === undefined ? null : current)); } return null; }), []);
  // reloadKey: the plan or sandbox type changed elsewhere (Settings), so what a restart applies may have too.
  useEffect(() => { load(); }, [load, reloadKey]);
  // A delete waiting for its backup: read again until it is done (or stopped).
  useEffect(() => {
    if (!info?.pendingDelete) { return undefined; }
    const timer = setInterval(load, PENDING_POLL_MS);
    return () => clearInterval(timer);
  }, [info?.pendingDelete, load]);

  const done = useCallback(async(text, tone = 'ok') => {
    if (!alive.current) { return; }
    setStatus({ tone, text });
    await load();
    onChanged?.();
  }, [load, onChanged]);

  const restartAndWait = async() => {
    const applies = pendingWords(info?.pending).text;
    setInfo((current) => ({ ...current, state: 'restarting' }));
    setStatus({
      tone: 'info',
      text: applies
        ? `Restarting your sandbox with ${applies}: it goes to sleep and wakes up again, in about a minute…`
        : 'Restarting your sandbox: its services and terminals stop and start again, in about a minute…',
    });
    const out = await restartMySandbox();
    const restarted = restartedText(out?.applied);
    // Slept and woken already (the gateway waited for it): nothing more to wait for.
    if (out?.applied && out.state === 'running') { return done(restarted); }
    const started = Date.now();
    let wentDown = false;
    while (alive.current && Date.now() - started < RESTART_MAX_MS) {
      await wait(POLL_MS);
      const now = await getSandboxState().catch(() => null);
      if (now?.state && now.state !== 'running') { wentDown = true; }
      if (now?.state === 'running' && (wentDown || Date.now() - started > 8000)) { return done(restarted); }
    }
    return done('Your sandbox is still starting: it is ready in a moment.', 'info');
  };

  const run = async(key, options = {}) => {
    if (busy) { return null; }
    setBusy(key);
    setStatus(null);
    try {
      if (key === 'restart') { await restartAndWait(); return null; }
      if (key === 'sleep') {
        setStatus({ tone: 'info', text: 'Putting your sandbox to sleep…' });
        await sleepMySandbox();
        markSleptByChoice();
        await done('Your sandbox is asleep. It wakes up the next time you open a project, and the apps that were running start again.');
        return null;
      }
      if (key === 'clearCaches') {
        setStatus({ tone: 'info', text: 'Clearing your preview caches…' });
        const out = await clearMyPreviewCaches();
        await done(clearedText(out));
        return out;
      }
      if (key === 'delete') {
        const out = await deleteMySandbox(options);
        // Home and Settings read the state from now on, never the overview: that would make the sandbox again.
        markSleptByChoice();
        if (out?.state === 'deleting') {
          await done(`A backup of your disks is being taken first (${keptText(out.backup)}); then your sandbox and its disks are deleted. This page updates when it is done.`, 'info');
        } else {
          await done(out?.keepVolume === false
            ? 'Your sandbox and its disks were deleted. A new, empty sandbox is made the next time you open Flow.'
            : 'Your sandbox was deleted. Your projects and data stay on your disks: the next time you open a project your sandbox is made again with them.');
        }
        return out;
      }
      return null;
    } catch (error) {
      if (alive.current) { setStatus({ tone: 'error', text: error.message || 'That did not work.' }); }
      if (key === 'delete') { throw error; }
      await load();
      return null;
    } finally {
      if (alive.current) { setBusy(null); }
    }
  };

  return { info, busy, status, run, reload: load };
}

/** Viewers, people with no sandbox, and gateways that do not offer it see nothing. */
export function useShowManage(info) {
  const permissions = usePermissions();
  // Only someone known to be a viewer is left out: no roles at all (not configured) is not a viewer.
  const viewer = Boolean(permissions && !permissions.loading && !permissions.error && permissions.roles?.size > 0 && permissions.isViewerOnly);
  return Boolean(info) && !viewer && (info.state !== 'none' || Boolean(info.pendingDelete));
}

/** Delete sandbox…: the disks are kept unless ticked; then the backup notice and the typed name. */
export function DeleteSandboxDialog({ open, info, busy = false, onCancel, onConfirm }) {
  const [disks, setDisks] = useState(false);
  const [backup, setBackup] = useState(true);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState(null);
  const typedId = useId();
  const name = info?.sandboxName || '';
  const backups = info?.backups || { enabled: false };
  useEffect(() => { if (open) { setDisks(false); setBackup(true); setTyped(''); setError(null); } }, [open]);
  const ready = !disks || (name && typed.trim() === name);
  const confirm = async() => {
    if (!ready || busy) { return; }
    setError(null);
    try {
      await onConfirm(disks ? { deleteDisks: true, confirm: typed.trim(), backup: backups.enabled ? backup : false } : {});
    } catch (err) {
      setError(err.code === 'confirm_mismatch' ? 'That is not your sandbox’s name: type it exactly as shown.' : (err.message || 'Your sandbox could not be deleted.'));
    }
  };
  return (
    <Modal
      className="msb-dialog"
      confirmDisabled={!ready}
      confirmText={disks ? 'Delete sandbox and disks' : 'Delete sandbox'}
      cancelText="Keep sandbox"
      focusCancel
      isDangerous
      isLoading={busy}
      isOpen={open}
      onCancel={onCancel}
      onClose={busy ? null : onCancel}
      onConfirm={confirm}
      title="Delete your sandbox?"
      type="custom"
      withFooter
    >
      <div className="msb-body">
        <span className={`msb-tag ${disks ? 'msb-tag-red' : 'msb-tag-blue'}`}>{disks ? 'Permanent' : 'Disks kept'}</span>
        <p className="msb-label">What happens</p>
        <ul className="msb-list">
          <li>Your sandbox stops now: running previews and terminals close.</li>
          {disks
            ? <li>Your disks are deleted: projects and data are gone for good{backups.enabled && backup ? ', except in the backup taken first' : ''}.</li>
            : <li>Your projects and data stay on your disks; the next time you open Flow your sandbox is created again with them.</li>}
        </ul>
        <label className="msb-check">
          <input checked={disks} disabled={busy} onChange={(event) => { setDisks(event.target.checked); setTyped(''); }} type="checkbox" />
          <span>Also delete my disks: projects and data are gone for good</span>
        </label>
        {disks && (
          <div className="msb-danger-zone">
            {backups.enabled ? (
              <label className="msb-check">
                <input checked={backup} disabled={busy} onChange={(event) => setBackup(event.target.checked)} type="checkbox" />
                <span>Take a backup first ({keptText(backups)}). You can restore it into your new sandbox from Settings › Sandbox › Backups.</span>
              </label>
            ) : (
              <p className="msb-warning">Backups are not set up on this Flow: nothing can bring your projects and data back.</p>
            )}
            <label className="msb-label" htmlFor={typedId}>Type <strong className="msb-mono">{name}</strong> to confirm</label>
            <input
              autoComplete="off"
              className="msb-input"
              disabled={busy}
              id={typedId}
              onChange={(event) => setTyped(event.target.value)}
              spellCheck={false}
              type="text"
              value={typed}
            />
          </div>
        )}
        {error && <p className="msb-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

DeleteSandboxDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  info: PropTypes.object,
  busy: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

function StatusLine({ status }) {
  if (!status) { return null; }
  return <p className={`msb-status msb-status-${status.tone}`} role={status.tone === 'error' ? 'alert' : 'status'}>{status.text}</p>;
}

StatusLine.propTypes = { status: PropTypes.object };

function useDeleteDialog(manage) {
  const [open, setOpen] = useState(false);
  const dialog = (
    <DeleteSandboxDialog
      busy={manage.busy === 'delete'}
      info={manage.info}
      onCancel={() => setOpen(false)}
      onConfirm={async(options) => { await manage.run('delete', options); setOpen(false); }}
      open={open}
    />
  );
  return [dialog, () => setOpen(true)];
}

/** Settings › Sandbox: "Manage your sandbox", one row per action. */
export function ManageSandboxSection({ onChanged = null, reloadKey = null }) {
  const manage = useManageSandbox({ onChanged, reloadKey });
  const [dialog, openDelete] = useDeleteDialog(manage);
  if (!useShowManage(manage.info)) { return null; }
  const { info, busy, status } = manage;
  return (
    <div aria-labelledby="set-sandbox-manage" className="msb-section" role="group">
      <h3 id="set-sandbox-manage">Manage your sandbox</h3>
      {info.pendingDelete && <p className="msb-status msb-status-info" role="status">Your sandbox is being deleted: a backup of its disks is taken first, then the disks go.</p>}
      {ACTIONS.map((action) => {
        const reason = blockedReason(action.key, info);
        return (
          <div className="settings-row" key={action.key}>
            <span className="settings-label">
              <strong>{action.label.replace('…', '')}</strong>
              <span>{action.what}</span>
              {action.key === 'restart' && pendingRestartText(info.pending) && (
                <span className="msb-pending" data-testid="restart-pending">{pendingRestartText(info.pending)}</span>
              )}
            </span>
            <Button
              danger={action.danger}
              disabled={Boolean(reason) || (Boolean(busy) && busy !== action.key)}
              loading={busy === action.key}
              onClick={() => (action.key === 'delete' ? openDelete() : manage.run(action.key))}
              title={reason || undefined}
            >
              {busy === 'restart' && action.key === 'restart' ? 'Restarting…' : action.label}
            </Button>
          </div>
        );
      })}
      <StatusLine status={status} />
      {dialog}
    </div>
  );
}

ManageSandboxSection.propTypes = { onChanged: PropTypes.func, reloadKey: PropTypes.string };

/** Home's sandbox card: the same actions behind "⋯". */
export function ManageSandboxMenu({ onChanged = null, reloadKey = null }) {
  const manage = useManageSandbox({ onChanged, reloadKey });
  const [dialog, openDelete] = useDeleteDialog(manage);
  if (!useShowManage(manage.info)) { return null; }
  const { info, busy, status } = manage;
  const applies = pendingRestartText(info.pending);
  const items = ACTIONS.map((action) => {
    const hint = action.key === 'restart' ? applies : '';
    return {
      key: action.key,
      label: hint ? <span className="msb-menu-item">{action.label}<span className="msb-pending">{hint}</span></span> : action.label,
      danger: action.danger,
      disabled: Boolean(busy) || Boolean(blockedReason(action.key, info)),
      title: blockedReason(action.key, info) || (hint ? `${action.what} ${hint}` : action.what),
    };
  });
  return (
    <span className="msb-menu">
      <Dropdown
        menu={{ items, onClick: ({ key }) => (key === 'delete' ? openDelete() : manage.run(key)) }}
        trigger={['click']}
      >
        <Button aria-label="Manage your sandbox" icon={<DotsThree size={18} weight="bold" />} loading={Boolean(busy)} />
      </Dropdown>
      <StatusLine status={status} />
      {dialog}
    </span>
  );
}

ManageSandboxMenu.propTypes = { onChanged: PropTypes.func, reloadKey: PropTypes.string };
